import React, { useState, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { hasPermission, createHistory, hasAtLeastOnePermission } from '../../sc-utilities';
import GradeList from './GradeList';
import { FindInPage } from '@material-ui/icons';
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import {editGradeVisibility} from "./store/gradeActions";

const styles = theme => ({
    root: {
        width: `100%`,
        height: 'auto',
        paddingLeft: theme.spacing(-1),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const Grade = props => {
    const tableRef = useRef();
    const { classes, columns, translations, sendMessage, permissions, onDownload, activeCustomerID, onEditGradeVisibility } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, GradeID: null, redirectPath: '' });

    const visible = columns && columns['Grade'];

    const tableColumns = useMemo(() => {

        const handleVisibilityChange = async data => {
            let visible = data.GradeVisibilities.length > 0 ? !data.GradeVisibilities[0].Visible : false;
            await onEditGradeVisibility({ ActiveCustomerID: activeCustomerID, GradeID: data.GradeID, Visible: visible });
            tableRef.current && tableRef.current.onQueryChange();
        };

        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: rowData => <>
                    {
                        hasPermission(permissions, 'grade_load') ? <Tooltip title={translations["grade_details"] || "Grade details"}>
                            <IconButton value={rowData.GradeID} aria-label={translations["grade_details"] || "Grade details"} onClick={() => redirectHandler(`/settings/grade/edit?GradeID=${rowData.GradeID}`)}>
                                <FindInPage />
                            </IconButton>
                        </Tooltip> : ''
                    }
                    {
                        hasPermission(permissions, 'grade_visibility_update') ?
                            <Tooltip title={translations["grade_visibility"] || "Grade visibility"}>
                                <IconButton component="div" value={rowData.GradeID} aria-label={translations["grade_visibility"] || "Grade visibility"}
                                            onClick={() => handleVisibilityChange(rowData)}>
                                    {rowData.GradeVisibilities && rowData.GradeVisibilities.length === 0 && <VisibilityIcon />}
                                    {rowData.GradeVisibilities && rowData.GradeVisibilities.length > 0 && rowData.GradeVisibilities[0].Visible && <VisibilityIcon />}
                                    {rowData.GradeVisibilities && rowData.GradeVisibilities.length > 0 && !rowData.GradeVisibilities[0].Visible && <VisibilityOffIcon />}
                                </IconButton>
                            </Tooltip> : ''
                    }
                </>
            },
            {
                title: translations["condition"] || 'Condition', field: 'Condition',
                hidden: visible ? visible['Condition'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Condition ? rowData.Condition : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["international_grading"] || 'International Grading', field: 'InternationalGrading',
                hidden: visible ? visible['InternationalGrading'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.InternationalGrading ? rowData.InternationalGrading : translations['short_not_assigned'] || 'n/a'}</Typography>;
                }
            },
            {
                title: translations["system"] || 'System', field: 'System',
                hidden: visible ? visible['System'] : false,
                filtering: true,
                lookup: { '1': translations["system"] || 'System', '0': translations["custom"] || 'Custom' },
                defaultFilter: ['0', '1'],
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.CustomerID ? translations["custom"] || 'Custom' : translations["system"] || 'System'}</Typography>;
                }
            },
            {
                title: translations["visible"] || 'Visible', field: 'Visible',
                hidden: visible ? visible['Visible'] : false,
                filtering: true,
                lookup: { '1': translations["visible"] || 'Visible', '0': translations["invisible"] || 'Non visible' },
                defaultFilter: ['0', '1'],
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.GradeVisibilities && rowData.GradeVisibilities.length === 0 ? translations["visible"] || 'Visible' :
                        rowData.GradeVisibilities?.length > 0 && rowData.GradeVisibilities[0].Visible ? translations["visible"] || 'Visible' : translations["invisible"] || 'Non visible'}</Typography>;
                }
            }
        ];
    }, [translations, visible, onEditGradeVisibility, activeCustomerID, permissions]);

    if (!hasAtLeastOnePermission(permissions, ['grade_load'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, GradeID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, GradeID: GradeID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'grade_list') ?
                <GradeList
                    tableRef={tableRef}
                    onDownload={onDownload}
                    sendMessage={sendMessage}
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                />
                :
                null}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        onEditGradeVisibility: typicalProblem => dispatch(editGradeVisibility(typicalProblem)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(Grade));
